import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import {
  ArrowLeft, ArrowRight, Check, Heart, Shield, Users, Bell,
  MapPin, FileText, ExternalLink, Globe,
  AlertTriangle, Home, Backpack, ClipboardList,
  PawPrint, BookOpen, Phone, TreePine, Cloud,
  Siren, BadgeCheck, ChevronRight, Zap,
  CalendarDays, Dog, Bird, Thermometer, Radio,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, Heading, EBtn } from "@/styles/editorial";

const COUNTRIES = {
  ES: {
    code: "ES", name: "España", flag: "ES", color: C.orange,
    emergency_phone: "112",
    resources: [
      { name: "AEMET - Alertas Meteorológicas", url: "https://www.aemet.es/es/eltiempo/prediccion/avisos" },
      { name: "Protección Civil", url: "https://www.proteccioncivil.es/" },
      { name: "ES-Alert (alertas a móviles)", url: "https://www.proteccioncivil.es/es-alert" },
    ],
  },
  EC: {
    code: "EC", name: "Ecuador", flag: "EC", color: "#ffd93d",
    emergency_phone: "911",
    resources: [
      { name: "Alertas Ecuador", url: "https://alertasecuador.gob.ec/lluvia" },
      { name: "INAMHI - Pronósticos", url: "https://inamhi.gob.ec/pronos/visor-pronosticos" },
      { name: "ECU 911", url: "https://www.ecu911.gob.ec/" },
      { name: "Gestión de Riesgos (SGR)", url: "https://www.gestionderiesgos.gob.ec/" },
    ],
  },
};

const PLATFORM_SECTIONS = [
  { id: "conecta", label: "CONECTA", icon: Heart, color: C.red, desc: "Refugios, voluntarios y necesidades activas. Coordínate para ayudar.", link: "/needs",
    features: ["Ver emergencias en tiempo real", "Ofrecer ayuda a necesidades", "Publicar animales perdidos", "Sistema de puntos y badges"] },
  { id: "prepara", label: "PREPARA", icon: Shield, color: C.blue, desc: "Prepárate para emergencias: mochila, plan de evacuación, tarjeta de domicilio.", link: "/prepara",
    features: ["Mochila de emergencia personalizada", "Plan de evacuación con animales", "Tarjeta de domicilio QR", "Calendario de preparación"] },
  { id: "aprende", label: "APRENDE", icon: BookOpen, color: C.lilac, desc: "Formación para familias y peques. Cursos, cuentos interactivos y juegos.", link: "/aprende",
    features: ["Curso para familias con animales", "KIDS: cuentos y juegos (3-14 años)", "Actividades descargables", "Certificados de completado"] },
  { id: "fauna", label: "FAUNA SALVAJE", icon: Bird, color: C.olive, desc: "Reporta animales silvestres heridos. Protocolos y centros de rescate.", link: "/fauna",
    features: ["Protocolo de actuación paso a paso", "Reportar animal herido con foto", "Centros de rescate geolocalizados", "Alerta automática a centros urgentes"] },
  { id: "tiempo", label: "TIEMPO Y ALERTAS", icon: Cloud, color: C.pink, desc: "Meteorología en tiempo real y recursos oficiales de alertas.", link: "/tiempo",
    features: ["Datos meteorológicos actualizados", "Pronóstico a 5 días", "Recursos oficiales: AEMET / INAMHI", "Radar e imágenes satelitales"] },
];

const ROLES = {
  volunteer: {
    label: "Voluntario/a", icon: Heart, color: C.orange,
    intro: "Como voluntario/a eres la pieza clave de la red. Tu ayuda marca la diferencia.",
    steps: [
      { title: "Regístrate", icon: Users, text: "Crea tu cuenta con email o Google. Es gratis y en 30 segundos.", action: "Ir a registro", link: "/auth" },
      { title: "Explora necesidades", icon: Heart, text: "Mira las necesidades activas: transporte, acogida, material, veterinarios...", action: "Ver necesidades", link: "/needs" },
      { title: "Ofrece tu ayuda", icon: Check, text: "Pulsa 'Ofrecer ayuda' en la necesidad que puedas cubrir. La protectora se pondrá en contacto.", tips: ["Sé específico en tu oferta", "Indica tu disponibilidad", "Responde rápido a los mensajes"] },
      { title: "Activa alertas", icon: Bell, text: "Recibe notificaciones de emergencias y necesidades urgentes en tu zona.", tips: ["Permite notificaciones push", "Suscríbete a Telegram y WhatsApp"] },
    ],
  },
  shelter: {
    label: "Protectora", icon: Home, color: C.blue,
    intro: "Como protectora puedes coordinar emergencias y gestionar la ayuda de voluntarios.",
    steps: [
      { title: "Regístrate como protectora", icon: Home, text: "Selecciona el rol 'Protectora/Refugio'. Añade los datos de tu organización.", action: "Ir a registro", link: "/auth" },
      { title: "Publica emergencias", icon: AlertTriangle, text: "Cuando ocurra una emergencia, crea una alerta para coordinar la respuesta rápida.", action: "Panel de protectora", link: "/shelter-dashboard" },
      { title: "Publica necesidades", icon: ClipboardList, text: "Crea necesidades específicas: voluntarios, materiales, acogida... Los voluntarios las verán.", tips: ["Indica la urgencia real", "Actualiza cuando se cubra", "Añade datos de contacto"] },
      { title: "Coordina voluntarios", icon: Users, text: "Gestiona las ofertas de ayuda. Contacta y coordina la logística.", tips: ["Responde rápido", "Agradece siempre la ayuda"] },
    ],
  },
  emergencias: {
    label: "Personal de emergencias", icon: Siren, color: C.red,
    intro: "Si eres bombero/a, policía, protección civil, Cruz Roja, veterinario/a... hay un rol específico para ti.",
    steps: [
      { title: "Solicita el rol", icon: BadgeCheck, text: "Regístrate y solicita el rol 'Personal de Emergencias'. Un admin verificará tu identidad.", action: "Ir a registro", link: "/auth" },
      { title: "Selecciona tu cuerpo", icon: Shield, text: "Indica tu cuerpo: Bomberos, Policía Nacional/Local, Protección Civil, Agents Rurals, Sanitarios, Cruz Roja, Veterinarios." },
      { title: "Publica alertas oficiales", icon: AlertTriangle, text: "Una vez verificado, puedes publicar alertas y necesidades con la autoridad de tu cargo.", tips: ["Puedes ser anónimo o mostrar tu cuerpo", "Tus publicaciones se marcan como 'Personal de Emergencias'"] },
      { title: "Coordina con la red", icon: Users, text: "Trabaja junto a protectoras y voluntarios para dar respuesta rápida a las emergencias." },
    ],
  },
};

const NAV_STEPS = [
  { id: "welcome", label: "Bienvenida", num: "01" },
  { id: "role", label: "Tu rol", num: "02" },
  { id: "explore", label: "Explora", num: "03" },
  { id: "resources", label: "Recursos", num: "04" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const countryCode = searchParams.get("country") || localStorage.getItem("emergencia_country") || "ES";
  const country = COUNTRIES[countryCode] || COUNTRIES.ES;

  const [step, setStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState("volunteer");
  const [roleStep, setRoleStep] = useState(0);
  const [expandedSection, setExpandedSection] = useState(null);

  const role = ROLES[selectedRole];

  return (
    <div className="min-h-screen" style={{ background: C.white, fontFamily: BODY }} data-testid="onboarding-page">

      {/* HEADER */}
      <header className="sticky top-0 z-50" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/conecta")} className="flex items-center gap-2 text-sm font-semibold"
              style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
              data-testid="onboarding-back-btn">
              <ArrowLeft className="w-4 h-4" /> Conecta
            </button>
            <div className="h-4 w-px hidden md:block" style={{ background: "rgba(255,255,255,0.15)" }} />
            <nav className="hidden md:flex items-center gap-1">
              {[
                { l: "Hub", h: "/" },
                { l: "Recursos", h: "/recursos/ciudadania/guias" },
                { l: "Prepara", h: "/prepara" },
                { l: "Aprende", h: "/aprende" },
              ].map((lnk) => (
                <button key={lnk.l} onClick={() => navigate(lnk.h)}
                  className="px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase transition-all"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.55)", background: "none", border: "none", cursor: "pointer" }}>
                  {lnk.l}
                </button>
              ))}
            </nav>
          </div>
          <div className="text-right">
            <h1 className="text-sm font-bold tracking-tight" style={{ fontFamily: BODY, color: C.white }}>
              Guía de inicio — {country.name}
            </h1>
            <p className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: C.orange }}>
              Emergencia Animal
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-3">
          <div className="flex gap-2">
            {NAV_STEPS.map((s, i) => (
              <button key={s.id} onClick={() => setStep(i)} data-testid={`onboarding-nav-${s.id}`}
                className="flex-1 py-2 flex items-center justify-center gap-2 text-[10px] font-semibold tracking-[0.1em] uppercase transition-all"
                style={{
                  fontFamily: MONO,
                  background: step === i ? C.orange : "rgba(255,255,255,0.06)",
                  color: step === i ? C.navy : "rgba(255,255,255,0.4)",
                  borderRadius: 2,
                  border: "none", cursor: "pointer",
                }}>
                {s.num} {s.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-16">

        {/* STEP 0: WELCOME */}
        {step === 0 && (
          <div data-testid="onboarding-step-welcome">
            <Reveal>
              <EditorialLabel text="BIENVENIDA" color={C.orange} />
              <Heading size="lg" className="mb-3">Bienvenido/a a Emergencia Animal</Heading>
              <p className="text-base md:text-lg max-w-2xl mb-10" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>
                Plataforma gratuita para proteger animales antes, durante y después de emergencias. Nadie se queda atrás. Ningún animal tampoco.
              </p>
            </Reveal>

            <div className="space-y-0 mb-10">
              {PLATFORM_SECTIONS.map((section, i) => {
                const isExpanded = expandedSection === section.id;
                return (
                  <Reveal key={section.id} delay={i * 0.04}>
                    <div
                      className="cursor-pointer transition-all"
                      style={{ borderBottom: `1px solid ${C.navy}10` }}
                      onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                      data-testid={`platform-section-${section.id}`}
                    >
                      <div className="flex items-center gap-4 py-5">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ background: section.color, borderRadius: 4 }}>
                          <section.icon className="w-5 h-5" style={{ color: C.white }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: section.color }}>{section.label}</p>
                          <p className="text-sm" style={{ color: C.gray }}>{section.desc}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: C.gray, transform: isExpanded ? "rotate(90deg)" : "none" }} />
                      </div>

                      {isExpanded && (
                        <div className="pl-14 pb-5">
                          <ul className="space-y-2 mb-4">
                            {section.features.map((f, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm" style={{ color: C.navy }}>
                                <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: section.color }} /> {f}
                              </li>
                            ))}
                          </ul>
                          <EBtn onClick={(e) => { e.stopPropagation(); navigate(section.link); }} bg={section.color} color={C.white} testId={`go-to-${section.id}`}>
                            Explorar {section.label} <ArrowRight className="w-4 h-4" />
                          </EBtn>
                        </div>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.2}>
              <div className="flex flex-wrap items-center justify-between gap-6 py-6 px-8" style={{ background: C.navy, borderRadius: 4 }}>
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-1" style={{ fontFamily: MONO, color: C.orange }}>
                    Tu país: {country.name}
                  </p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
                    Teléfono de emergencias: <span className="font-bold text-lg" style={{ color: C.white }}>{country.emergency_phone}</span>
                  </p>
                </div>
                <EBtn onClick={() => setStep(1)} bg={C.orange} color={C.navy} testId="onboarding-start-btn">
                  Empezar <ArrowRight className="w-4 h-4" />
                </EBtn>
              </div>
            </Reveal>
          </div>
        )}

        {/* STEP 1: YOUR ROLE */}
        {step === 1 && (
          <div data-testid="onboarding-step-role">
            <Reveal>
              <EditorialLabel number="02" text="TU ROL" color={C.blue} />
              <Heading size="lg" className="mb-2">¿Cómo quieres ayudar?</Heading>
              <p className="text-sm mb-8" style={{ color: C.gray }}>Selecciona tu rol para ver la guía específica.</p>
            </Reveal>

            <div className="flex flex-wrap gap-2 mb-8">
              {Object.entries(ROLES).map(([key, r]) => (
                <button key={key} onClick={() => { setSelectedRole(key); setRoleStep(0); }}
                  className="px-5 py-2.5 text-xs font-semibold tracking-wider flex items-center gap-2 transition-all"
                  style={{
                    fontFamily: BODY,
                    background: selectedRole === key ? r.color : "transparent",
                    color: selectedRole === key ? C.white : C.navy,
                    border: `1px solid ${selectedRole === key ? r.color : C.navy + "20"}`,
                    borderRadius: 4, cursor: "pointer",
                  }}
                  data-testid={`role-tab-${key}`}>
                  <r.icon className="w-4 h-4" /> {r.label}
                </button>
              ))}
            </div>

            <Reveal>
              <div className="mb-8 py-4 px-5" style={{ borderLeft: `3px solid ${role.color}`, background: `${role.color}08` }}>
                <p className="text-sm" style={{ color: C.navy, lineHeight: 1.7 }}>{role.intro}</p>
              </div>
            </Reveal>

            <div className="space-y-4">
              {role.steps.map((s, i) => {
                const isActive = roleStep === i;
                return (
                  <Reveal key={i} delay={i * 0.05}>
                    <div className="cursor-pointer transition-all" onClick={() => setRoleStep(i)}
                      style={{ borderLeft: isActive ? `3px solid ${role.color}` : `3px solid ${C.navy}10`, background: isActive ? C.white : "transparent", padding: "16px 20px", borderRadius: 2 }}
                      data-testid={`role-step-${i}`}>
                      <div className="flex items-start gap-4">
                        <span className="w-7 h-7 flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ fontFamily: MONO, background: isActive ? role.color : `${C.navy}08`, color: isActive ? C.white : C.navy, borderRadius: 4 }}>
                          {i + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-bold mb-1" style={{ color: C.navy }}>{s.title}</p>
                          <p className="text-sm" style={{ color: C.gray, lineHeight: 1.6 }}>{s.text}</p>
                          {isActive && s.tips && (
                            <ul className="mt-3 space-y-1.5">
                              {s.tips.map((t, j) => (
                                <li key={j} className="flex items-start gap-2 text-xs" style={{ color: C.gray }}>
                                  <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: C.olive }} /> {t}
                                </li>
                              ))}
                            </ul>
                          )}
                          {isActive && s.action && (
                            <EBtn onClick={(e) => { e.stopPropagation(); navigate(s.link); }} bg={role.color} color={C.white} className="mt-3" testId={`role-action-${i}`}>
                              {s.action} <ArrowRight className="w-3.5 h-3.5" />
                            </EBtn>
                          )}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <NavButtons onPrev={() => setStep(0)} onNext={() => setStep(2)} nextLabel="Explorar la plataforma" testId="role-next-btn" />
          </div>
        )}

        {/* STEP 2: EXPLORE */}
        {step === 2 && (
          <div data-testid="onboarding-step-explore">
            <Reveal>
              <EditorialLabel number="03" text="EXPLORA" color={C.olive} />
              <Heading size="lg" className="mb-2">Explora la plataforma</Heading>
              <p className="text-sm mb-8" style={{ color: C.gray }}>
                Estas son las herramientas a tu disposición. Toca cada sección para saber más.
              </p>
            </Reveal>

            <div className="space-y-4">
              <FeatureShowcase icon={Bird} color={C.olive} label="FAUNA SALVAJE" testId="explore-fauna"
                subtitle={countryCode === "EC" ? "Ministerio de Ambiente: 1800-262-368" : "112 / SEPRONA"}
                description="¿Has encontrado un animal silvestre herido? La app te guía paso a paso."
                items={[
                  { icon: FileText, text: "Protocolo de actuación: qué hacer y qué NO hacer" },
                  { icon: MapPin, text: "Centros de rescate geolocalizados — encuentra el más cercano a ti en tiempo real" },
                  { icon: PawPrint, text: "Formulario de reporte con foto desde el móvil" },
                  { icon: Bell, text: "Alerta automática por email al centro en casos urgentes" },
                ]}
                cta="IR A FAUNA SALVAJE" link="/fauna" navigate={navigate}
                extraNote="Recomendamos activar la geolocalización para encontrar automáticamente el centro de rescate más cercano. Tus datos de ubicación no se almacenan ni se comparten."
              />
              <FeatureShowcase icon={Cloud} color={C.pink} label="TIEMPO Y ALERTAS" testId="explore-tiempo"
                subtitle={countryCode === "EC" ? "INAMHI · SGR · ECU 911" : "AEMET · ES-Alert · Protección Civil"}
                description="Información meteorológica y recursos oficiales de alertas."
                items={[
                  { icon: Thermometer, text: "Temperatura, viento y condiciones actuales" },
                  { icon: CalendarDays, text: "Pronóstico a 5 días para tu zona" },
                  { icon: Radio, text: countryCode === "EC" ? "Visor INAMHI integrado con datos satelitales" : "Radar AEMET y predicciones por municipio" },
                  { icon: AlertTriangle, text: "Enlaces directos a fuentes oficiales de alertas" },
                ]}
                cta="IR A TIEMPO Y ALERTAS" link="/tiempo" navigate={navigate}
              />
              <FeatureShowcase icon={Shield} color={C.blue} label="PREPARA" testId="explore-prepara"
                subtitle="Antes de la emergencia, prepárate"
                description="Herramientas para estar listo cuando llegue una emergencia."
                items={[
                  { icon: Backpack, text: "Mochila de emergencia personalizada para ti y tus animales" },
                  { icon: MapPin, text: "Plan de evacuación: rutas, destinos, transporte" },
                  { icon: FileText, text: "Tarjeta de domicilio con QR (datos de emergencia)" },
                  { icon: CalendarDays, text: "Calendario de preparación con recordatorios" },
                ]}
                cta="IR A PREPARA" link="/prepara" navigate={navigate}
              />
              <FeatureShowcase icon={BookOpen} color={C.lilac} label="APRENDE KIDS" testId="explore-kids"
                subtitle="Para peques de 3 a 14 años"
                description="Cuentos, juegos y actividades para que los más pequeños aprendan a proteger a los animales."
                items={[
                  { icon: Dog, text: "Cuentos interactivos con quiz y recompensas" },
                  { icon: PawPrint, text: "3 niveles: Pequeños Héroes (3-6), Exploradores (6-10), Guardianes (10-14)" },
                  { icon: TreePine, text: "Temas: tormentas, incendios, primeros auxilios, mochila de emergencia" },
                ]}
                cta="IR A APRENDE KIDS" link="/aprende/kids" navigate={navigate}
              />
            </div>

            <NavButtons onPrev={() => setStep(1)} onNext={() => setStep(3)} nextLabel="Recursos y contactos" testId="explore-next-btn" />
          </div>
        )}

        {/* STEP 3: RESOURCES */}
        {step === 3 && (
          <div data-testid="onboarding-step-resources">
            <Reveal>
              <EditorialLabel number="04" text="RECURSOS" color={C.red} />
              <Heading size="lg" className="mb-2">Recursos y contactos</Heading>
              <p className="text-sm mb-8" style={{ color: C.gray }}>
                Guarda estos contactos ANTES de que llegue la emergencia.
              </p>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Emergency Phone */}
              <Reveal>
                <div className="p-6" style={{ background: C.orange, borderRadius: 4 }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Phone className="w-4 h-4" style={{ color: C.white }} />
                    <p className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.7)" }}>
                      Emergencias {country.name}
                    </p>
                  </div>
                  <a href={`tel:${country.emergency_phone}`} className="text-5xl font-bold block mb-2" style={{ fontFamily: DISPLAY, color: C.white }} data-testid="emergency-phone-link">
                    {country.emergency_phone}
                  </a>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>Línea de emergencias de {country.name}</p>
                  {countryCode === "EC" && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>Bomberos: <b>102</b> · Cruz Roja: <b>131</b> · Policía: <b>101</b></p>
                      <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>SGR: <b>1800-747-911</b> · Fauna: <b>1800-262-368</b></p>
                    </div>
                  )}
                </div>
              </Reveal>

              {/* Official Resources */}
              <Reveal delay={0.1}>
                <div className="p-6" style={{ background: C.white, border: `1px solid ${C.navy}15`, borderRadius: 4 }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="w-4 h-4" style={{ color: C.blue }} />
                    <p className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: C.blue }}>Recursos oficiales</p>
                  </div>
                  <div className="space-y-2">
                    {country.resources.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-between py-2.5 px-3 transition-all hover:-translate-y-0.5"
                        style={{ background: `${C.navy}05`, borderRadius: 4 }}
                        data-testid={`resource-link-${i}`}>
                        <span className="text-xs font-semibold" style={{ fontFamily: BODY, color: C.navy }}>{r.name}</span>
                        <ExternalLink className="w-3.5 h-3.5" style={{ color: C.blue }} />
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>

              {/* Social Channels */}
              <Reveal delay={0.15}>
                <div className="p-6" style={{ background: C.white, border: `1px solid ${C.navy}15`, borderRadius: 4 }}>
                  <p className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: MONO, color: C.navy }}>Canales de comunicación</p>
                  <div className="space-y-2">
                    <a href="https://t.me/+emergenciaanimal" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 transition-all hover:-translate-y-0.5"
                      style={{ background: "#0088cc", borderRadius: 4 }}
                      data-testid="channel-telegram">
                      <span className="text-sm font-bold text-white">TG</span>
                      <span className="text-xs font-bold text-white">Telegram — Emergencia Animal</span>
                    </a>
                    <a href="https://whatsapp.com/channel/0029VbCWhL21t90VVBCzzp1U" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 transition-all hover:-translate-y-0.5"
                      style={{ background: "#25D366", borderRadius: 4 }}
                      data-testid="channel-whatsapp">
                      <span className="text-sm font-bold text-white">WA</span>
                      <span className="text-xs font-bold text-white">WhatsApp — Canal Oficial</span>
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Quick Access */}
            <Reveal>
              <div className="p-6" style={{ background: C.navy, borderRadius: 4 }}>
                <p className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>Accesos rápidos</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { icon: AlertTriangle, label: "Emergencias", link: "/needs", color: C.orange },
                    { icon: Bird, label: "Fauna", link: "/fauna", color: C.olive },
                    { icon: Cloud, label: "Tiempo", link: "/tiempo", color: C.pink },
                    { icon: Shield, label: "Prepara", link: "/prepara", color: C.blue },
                    { icon: BookOpen, label: "KIDS", link: "/aprende/kids", color: C.lilac },
                  ].map((item, i) => (
                    <button key={i} onClick={() => navigate(item.link)}
                      className="p-3 text-center transition-all hover:-translate-y-1"
                      style={{ background: item.color, borderRadius: 4, border: "none", cursor: "pointer" }}
                      data-testid={`quick-access-${i}`}>
                      <item.icon className="w-5 h-5 mx-auto mb-1.5 text-white" />
                      <span className="text-[10px] font-semibold text-white uppercase block" style={{ fontFamily: MONO }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* CTA Final */}
            <Reveal delay={0.1}>
              <div className="mt-8 text-center py-10">
                <Heading size="sm" className="mb-2">¡Todo listo!</Heading>
                <p className="text-sm mb-6" style={{ color: C.gray }}>
                  Ya conoces las herramientas. Ahora empieza a proteger.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {user ? (
                    <EBtn onClick={() => navigate("/")} bg={C.orange} color={C.navy} testId="go-to-hub-btn">
                      Ir al Hub <ArrowRight className="w-4 h-4" />
                    </EBtn>
                  ) : (
                    <>
                      <EBtn onClick={() => navigate("/auth")} bg={C.blue} color={C.white} testId="go-to-register-btn">
                        Regístrate <ArrowRight className="w-4 h-4" />
                      </EBtn>
                      <EBtn onClick={() => navigate("/")} outline bg={C.navy} testId="explore-hub-btn">
                        Explorar sin cuenta <ArrowRight className="w-4 h-4" />
                      </EBtn>
                    </>
                  )}
                </div>
              </div>
            </Reveal>

            <div className="flex justify-start">
              <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm font-semibold"
                style={{ fontFamily: BODY, color: C.navy, background: "none", border: "none", cursor: "pointer" }}>
                <ArrowLeft className="w-4 h-4" /> Anterior
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* ─── NAV BUTTONS ─── */
function NavButtons({ onPrev, onNext, nextLabel, testId }) {
  return (
    <div className="flex justify-between mt-10">
      <button onClick={onPrev} className="flex items-center gap-1 text-sm font-semibold"
        style={{ fontFamily: BODY, color: C.navy, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft className="w-4 h-4" /> Anterior
      </button>
      <EBtn onClick={onNext} bg={C.navy} color={C.white} testId={testId}>
        {nextLabel} <ArrowRight className="w-4 h-4" />
      </EBtn>
    </div>
  );
}

/* ─── FEATURE SHOWCASE ─── */
function FeatureShowcase({ icon: Icon, color, label, subtitle, description, items, cta, link, navigate, testId, extraNote }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="transition-all" style={{ borderBottom: `1px solid ${C.navy}10` }} data-testid={testId}>
      <div className="flex items-center gap-4 py-5 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ background: color, borderRadius: 4 }}>
          <Icon className="w-5 h-5" style={{ color: C.white }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color }}>{label}</p>
          <p className="text-sm" style={{ color: C.gray }}>{subtitle}</p>
        </div>
        <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform"
          style={{ color: C.gray, transform: open ? "rotate(90deg)" : "none" }} />
      </div>
      {open && (
        <div className="pl-14 pb-5">
          <p className="text-sm mb-4" style={{ color: C.navy, lineHeight: 1.6 }}>{description}</p>
          <ul className="space-y-2.5 mb-4">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: C.navy }}>
                <item.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} /> {item.text}
              </li>
            ))}
          </ul>
          <EBtn onClick={() => navigate(link)} bg={color} color={C.white} testId={testId + "-cta"}>
            {cta} <ArrowRight className="w-3.5 h-3.5" />
          </EBtn>
          {extraNote && (
            <div className="mt-3 py-3 px-4" style={{ borderLeft: `2px solid ${color}`, background: `${color}08` }}>
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color }} />
                <p className="text-[11px]" style={{ color: C.gray, lineHeight: 1.5 }}>{extraNote}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
